"use client";
import React, { Suspense, lazy, useMemo, useRef, useCallback, useState, useEffect } from 'react';

const NoiseShaderBackground = lazy(() => import('../components/ui/NoiseShaderBackground'));
const VideoHero = lazy(() => import('../components/sections/VideoHero'));
const AboutMe = lazy(() => import('../components/sections/AboutMe'));
const Testimonial = lazy(() => import('../components/sections/Testimonial'));
const EGallery = lazy(() => import('../components/sections/EGallery'));
const Contact = lazy(() => import('../components/sections/Contact'));
const Footer = lazy(() => import('../components/layout/Footer'));
const Navigation = lazy(() => import('../components/layout/Navigation'));
const AnimateOnLoad = lazy(() => import('../components/ui/AnimateOnLoad'));

import { NavigationSkeleton, HeroSkeleton, SectionSkeleton, FooterSkeleton } from '../components/skeletons/Skeletons';

const SectionPlaceholder = ({ height = '100vh' }: { height?: string }) => <div style={{ height }} />;

export default function HomePageClient() {
  const [showBackground, setShowBackground] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [showNavigation, setShowNavigation] = useState(false);
  const [aboutVisible, setAboutVisible] = useState(false);
  const [galleryVisible, setGalleryVisible] = useState(false);
  const [testimonialVisible, setTestimonialVisible] = useState(false);
  const [contactVisible, setContactVisible] = useState(false);
  const [showFooter, setShowFooter] = useState(false);
  
  const homeRef = useRef<HTMLDivElement>(null);
  const aboutRef = useRef<HTMLElement>(null);
  const testimonialRef = useRef<HTMLElement>(null);
  const galleryRef = useRef<HTMLElement>(null);
  const contactRef = useRef<HTMLElement>(null);
  const aboutContainerRef = useRef<HTMLDivElement>(null);
  const testimonialContainerRef = useRef<HTMLDivElement>(null);
  const galleryContainerRef = useRef<HTMLDivElement>(null);
  const contactContainerRef = useRef<HTMLDivElement>(null);

  const scrollToSection = useCallback((sectionRef: React.RefObject<HTMLElement | null>) => {
    sectionRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);
  
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
          if (entry.isIntersecting) {
            const target = entry.target;
            if (target === aboutContainerRef.current) {
              setAboutVisible(true);
            } else if (target === testimonialContainerRef.current) {
              setTestimonialVisible(true);
            } else if (target === galleryContainerRef.current) {
              setGalleryVisible(true);
            } else if (target === contactContainerRef.current) {
              setContactVisible(true);
            }
            observer.unobserve(target);
          }
      });
    }, { rootMargin: '1000px' });

    if (aboutContainerRef.current) observer.observe(aboutContainerRef.current);
    if (testimonialContainerRef.current) observer.observe(testimonialContainerRef.current);
    if (galleryContainerRef.current) observer.observe(galleryContainerRef.current);
    if (contactContainerRef.current) observer.observe(contactContainerRef.current);
    return () => observer.disconnect();
  }, []);
  
  // Defer loading background, navigation and footer until browser is idle
  useEffect(() => {
    const idleCallback = window.requestIdleCallback || ((cb: () => void) => setTimeout(cb, 0));
    const bgHandle = idleCallback(() => setShowBackground(true));
    const navHandle = idleCallback(() => setShowNavigation(true));
    const footerHandle = idleCallback(() => setShowFooter(true));
    return () => {
      if ('cancelIdleCallback' in window) {
        (window as any).cancelIdleCallback(bgHandle);
        (window as any).cancelIdleCallback(navHandle);
        (window as any).cancelIdleCallback(footerHandle);
      }
    };
  }, []);

  const navLinks = useMemo(() => [
    { name: 'home', name_pl: 'start', ref: homeRef, id: 'home' },
    { name: 'about', name_pl: 'o mnie', ref: aboutRef, id: 'about' },
    { name: 'testimonials', name_pl: 'referencje', ref: testimonialRef, id: 'testimonials' },
    { name: 'gallery', name_pl: 'galeria', ref: galleryRef, id: 'gallery' },
    { name: 'contact', name_pl: 'kontakt', ref: contactRef, id: 'contact' }
  ], []);

  return (
    <main className="bg-fox-black overflow-x-hidden font-sans text-white">
      <Suspense fallback={null}>
        {showBackground && (
          <AnimateOnLoad from={{ opacity: 0 }} to={{ opacity: 1, duration: 1.5 }}>
            <Suspense fallback={null}>
              <NoiseShaderBackground />
            </Suspense>
          </AnimateOnLoad>
        )}
      </Suspense>
      
      <Suspense fallback={<NavigationSkeleton />}>
        {showNavigation && (
          <Navigation
            navLinks={navLinks}
            activeSection={activeSection}
            scrollToSection={scrollToSection}
          />
        )}
      </Suspense>
      
      <div ref={homeRef} id="home">
        <Suspense fallback={<HeroSkeleton />}>
            <VideoHero scrollToSection={() => scrollToSection(aboutRef)} />
        </Suspense>
      </div>

      <div ref={aboutContainerRef}>
        {aboutVisible ? (
            <Suspense fallback={<SectionSkeleton />}>
              <AboutMe ref={aboutRef} id="about" />
            </Suspense>
        ) : <SectionSkeleton />}
      </div>
      
      <div ref={testimonialContainerRef}>
        {testimonialVisible ? (
            <Suspense fallback={<SectionSkeleton />}>
              <Testimonial ref={testimonialRef} id="testimonials" />
            </Suspense>
        ) : <SectionSkeleton />}
      </div>

      <div ref={galleryContainerRef}>
        {galleryVisible ? (
            <Suspense fallback={<SectionSkeleton />}>
              <EGallery ref={galleryRef} id="gallery" />
            </Suspense>
        ) : <SectionSkeleton />}
      </div>

      <div ref={contactContainerRef}>
        {contactVisible ? (
            <Suspense fallback={<SectionSkeleton hasImage={false} />}>
              <Contact ref={contactRef} id="contact" />
            </Suspense>
        ) : <SectionSkeleton hasImage={false} />}
      </div>
      
      <Suspense fallback={<FooterSkeleton />}>
        {showFooter && (
          <AnimateOnLoad from={{ opacity: 0 }} to={{ opacity: 1, duration: 1.5 }}>
            <Footer />
          </AnimateOnLoad>
        )}
      </Suspense>
    </main>
  );
}
